/**
 * THE COMPOUNDING LIFE — homepage behaviour (DEV theme).
 * Reveals, hero parallax, scroll-driven progress, and the purchase form.
 * The purchase form uses Horizon's own cart pipeline: Shopify /cart/add,
 * Horizon's CartAddEvent, and the native cart drawer. Nothing is replaced.
 * v10: if the item is already in the cart, "Add to cart" just opens the cart
 * (no second unit added by accident), and double taps are ignored.
 */
import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
const root = document.documentElement;
root.classList.add('tcl-js');

/* ---------- Reveal ---------- */
function initReveal(scope = document) {
  const els = scope.querySelectorAll('.tcl [data-reveal], .tcl [data-lines], .tcl-field, .tcl-tx__row, .tcl-hz__title');
  if (reduce.matches || !('IntersectionObserver' in window)) {
    els.forEach((el) => el.classList.add('is-in'));
    return;
  }
  const io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          e.target.classList.add('is-in');
          io.unobserve(e.target);
        }
      }
    },
    { rootMargin: '0px 0px -12% 0px', threshold: 0.12 }
  );
  els.forEach((el) => io.observe(el));
}

/* ---------- Scroll loop (parallax + progress) ---------- */
const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
let progressEls = [];
let parallaxEls = [];
let ticking = false;

function measure() {
  progressEls = [...document.querySelectorAll('.tcl [data-progress]')];
  parallaxEls = [...document.querySelectorAll('.tcl [data-parallax]')];
  // A 'pin' track only behaves as pinned when its inner panel is actually sticky
  for (const el of progressEls) {
    if (el.dataset.progress !== 'pin') continue;
    const pin = el.querySelector('[data-pin]');
    el.dataset.pinned = pin && getComputedStyle(pin).position === 'sticky' ? 'true' : 'false';
  }
}

function update() {
  ticking = false;
  const vh = window.innerHeight;

  for (const el of progressEls) {
    const mode = el.dataset.progress;
    const box = (el.closest('[data-progress-root]') || el).getBoundingClientRect();
    let p;
    const pinned = mode === 'pin' && el.dataset.pinned === 'true';
    if (pinned) {
      // 0 when the pinned track reaches the top, 1 when it is about to release
      p = clamp(-box.top / Math.max(1, box.height - vh));
    } else {
      // 0 when the element enters at 85% of the viewport, 1 when it reaches 35%
      p = clamp((vh * 0.85 - box.top) / Math.max(1, box.height + vh * 0.5));
    }
    el.style.setProperty('--p', p.toFixed(4));

    const steps = el.querySelectorAll('[data-step]');
    const n = steps.length;
    steps.forEach((s, i) => s.classList.toggle('is-active', p >= (i / n) * 0.92 + 0.02));

    const counter = el.querySelector('[data-year]') || el.closest('.tcl')?.querySelector('[data-year]');
    if (counter) {
      const y = Math.round(p * 10);
      counter.textContent = y === 0 ? 'Now' : `Year ${String(y).padStart(2, '0')}`;
    }
  }

  for (const el of parallaxEls) {
    const r = el.getBoundingClientRect();
    if (r.bottom < -200 || r.top > vh + 200) continue;
    const speed = parseFloat(el.dataset.parallax) || 0.1;
    const centre = r.top + r.height / 2 - vh / 2;
    el.style.setProperty('--py', `${(-centre * speed).toFixed(1)}px`);
  }
}

function onScroll() {
  if (ticking) return;
  ticking = true;
  requestAnimationFrame(update);
}

function initScroll() {
  measure();
  if (reduce.matches) {
    progressEls.forEach((el) => {
      el.style.setProperty('--p', '1');
      el.querySelectorAll('[data-step]').forEach((s) => s.classList.add('is-active'));
    });
    return;
  }
  update();
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', () => { measure(); onScroll(); }, { passive: true });
}

/* ---------- Hero entrance ---------- */
function initHero() {
  const hero = document.querySelector('.tcl-hero');
  if (!hero) return;
  requestAnimationFrame(() => {
    hero.classList.add('is-in');
    // after the slow entrance, let parallax track scroll without easing lag
    setTimeout(() => hero.classList.add('is-settled'), 3300);
  });
}

/* ---------- Purchase: native Shopify cart + Horizon drawer ---------- */
function openDrawerOrCart(T) {
  const drawer = document.querySelector('cart-drawer-component');
  if (drawer && typeof drawer.open === 'function') {
    drawer.open();
    return true;
  }
  if (!drawer) window.location.href = T.routes.cart_url || '/cart';
  return false;
}

let busy = false;

function initPurchase(scope = document) {
  scope.querySelectorAll('form[data-tcl-buy]').forEach((form) => {
    if (form.dataset.bound) return;
    form.dataset.bound = 'true';

    form.addEventListener('submit', async (event) => {
      // Horizon declares `Theme` as a global constant (not a window property)
      const T = typeof Theme !== 'undefined' ? Theme : window.Theme;
      if (!T?.routes?.cart_add_url) return; // fall back to normal POST → /cart
      event.preventDefault();
      if (busy) return; // ignore double taps while a request is running
      busy = true;

      const button = form.querySelector('button[type="submit"]');
      const status = form.querySelector('[data-status]');
      const say = (msg, state = '') => {
        if (!status) return;
        status.textContent = msg;
        status.dataset.state = state;
      };

      button?.setAttribute('aria-busy', 'true');
      button && (button.disabled = true);
      say('');

      const formData = new FormData(form);
      const variantId = Number(formData.get('id'));

      try {
        // Already in the cart? Just open the cart instead of adding a second one.
        const current = await fetch(`${T.routes.cart_url || '/cart'}.js`).then((r) => r.json()).catch(() => null);
        if (current && current.items && current.items.some((i) => i.variant_id === variantId)) {
          say('Already in your cart.');
          if (window.clarity) window.clarity('event', 'atc_already_in_cart');
          openDrawerOrCart(T);
          return;
        }

        const sectionIds = [...document.querySelectorAll('cart-items-component')]
          .map((c) => c instanceof HTMLElement && c.dataset.sectionId)
          .filter(Boolean);
        if (sectionIds.length) formData.append('sections', sectionIds.join(','));

        const cfg = fetchConfig('javascript', { body: formData });
        const res = await fetch(T.routes.cart_add_url, {
          ...cfg,
          headers: { ...cfg.headers, Accept: 'text/html' },
        });
        const data = await res.json();

        if (data.status) {
          form.dispatchEvent(new CartErrorEvent(form.id || '', data.message, data.description, data.errors));
          say(data.description || data.message || 'This item could not be added.', 'error');
          return;
        }

        const cart = await fetch(`${T.routes.cart_url || '/cart'}.js`).then((r) => r.json()).catch(() => null);

        form.dispatchEvent(
          new CartAddEvent(cart ?? undefined, String(formData.get('id')), {
            source: 'tcl-purchase',
            itemCount: cart?.item_count ?? undefined,
            productId: form.dataset.productId,
            sections: data.sections,
          })
        );

        say(form.dataset.addedText || 'Added to your cart.');

        const drawer = document.querySelector('cart-drawer-component');
        if (drawer && typeof drawer.open === 'function' && !drawer.hasAttribute('auto-open')) {
          drawer.open();
        } else if (!drawer) {
          window.location.href = T.routes.cart_url || '/cart';
        }
      } catch (err) {
        console.error(err);
        say('Something went wrong. Please try again.', 'error');
      } finally {
        busy = false;
        button?.removeAttribute('aria-busy');
        button && (button.disabled = false);
      }
    });
  });
}

/* ---------- Boot ---------- */
function boot() {
  initReveal();
  initScroll();
  initHero();
  initPurchase();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}

// Theme editor: re-run when a section is re-rendered
document.addEventListener('shopify:section:load', (e) => {
  initReveal(e.target);
  initPurchase(e.target);
  measure();
  update();
});
